import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import { useContentColumnsBreakpoint } from '../../../providers/AssistantSidebarProvider';

interface IEntityPaneBodyProps {
  /** Full-width content above the two columns at the wide breakpoint. */
  primaryElement?: React.ReactNode;
  /** Main column at the wide breakpoint. */
  leftColumn?: React.ReactNode;
  /** Side column at the wide breakpoint (347px). */
  rightColumn?: React.ReactNode;
  /** When stacked (narrow), render these in order. When omitted, order is primary, right, left. */
  singleColumnOrderOverride?: React.ReactNode[];
  className?: string;
}

/**
 * Pane body with an optional primary block and two-column layout. When narrow
 * it either stacks singleColumnOrderOverride in order (if provided) or shows
 * right then left; when wide it shows primary then left | right.
 *
 * The "wide" breakpoint is normally `xl`, but shifts to `2xl` while the
 * assistant side panel is open (the panel consumes horizontal space, so two
 * columns only fit one breakpoint later). The threshold arrives via
 * {@link useContentColumnsBreakpoint} — a layout-neutral hook — so this generic
 * primitive stays ignorant of the assistant itself.
 *
 * `flex-1` lets the body fill the pane below the sticky header so short content
 * still stretches the two columns to full height, and grow past it so the page
 * scroller reveals the overflow. The body is not itself a scroll container:
 * html/body are locked and one full-width scroller per route owns vertical
 * scroll (see scenes/HomeScene.tsx and ORN-548).
 */
export const EntityPaneBody = ({
  primaryElement,
  leftColumn,
  rightColumn,
  singleColumnOrderOverride,
  className,
}: IEntityPaneBodyProps) => {
  // Both hooks fire unconditionally; pick by the column breakpoint so a
  // changing threshold never reorders the hook calls.
  const columnsBreakpoint = useContentColumnsBreakpoint();
  const isXl = useBreakpoint('xl');
  const is2xl = useBreakpoint('2xl');
  const isWide = columnsBreakpoint === '2xl' ? is2xl : isXl;

  // Padding scales with viewport per Figma: 16px (<sm) → 20px (sm, md, lg) → 32px (xl+).
  // It tracks the viewport independently of the column count.
  const bodyClassName = cx('flex w-full flex-1 flex-col gap-5 px-4 pb-4 sm:px-5 sm:pb-5 xl:px-8 xl:pb-8', className);
  const hasSingleColumnOrderOverride = singleColumnOrderOverride != null && singleColumnOrderOverride.length > 0;
  const hasColumns = leftColumn != null || rightColumn != null;

  // Narrow: single-column stacked in override order (only one tree mounted)
  if (hasSingleColumnOrderOverride && !isWide) {
    return (
      <div className={bodyClassName}>
        {singleColumnOrderOverride.map((section, index) => (
          <div key={index} className="flex min-w-0 flex-col gap-5">
            {section}
          </div>
        ))}
      </div>
    );
  }

  // Desktop (or no override): primary element + two-column layout
  return (
    <div className={bodyClassName}>
      {primaryElement != null && <div className="flex flex-col gap-5">{primaryElement}</div>}
      {hasColumns && (
        <div className={cx('flex min-w-0 flex-1 flex-col gap-5', isWide && 'flex-row')}>
          {/* When no singleColumnOrderOverride, order-1/order-2 make the stacked (narrow) order primary → right → left (DOM is primary → left → right); at the wide breakpoint no order class is applied so DOM order wins. */}
          {leftColumn != null && (
            <div
              className={cx(
                'flex min-w-0 flex-1 flex-col gap-5',
                !hasSingleColumnOrderOverride && !isWide && 'order-2'
              )}
            >
              {leftColumn}
            </div>
          )}
          {rightColumn != null && (
            <div
              className={cx(
                'flex flex-col gap-5',
                isWide ? 'w-[347px] shrink-0' : 'w-full',
                !hasSingleColumnOrderOverride && !isWide && 'order-1'
              )}
            >
              {rightColumn}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface IEntityPaneProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Vertical pane shell: a sticky EntityPaneHeader above a flowing EntityPaneBody.
 *
 * `min-h-full` fills the page scroller when content is short (so the body's
 * flex children stretch to full height) and grows past it when content
 * overflows. Growing past it is load-bearing for the sticky header: the
 * header's containing block is this element, so it must span the full content
 * height for the header to stay pinned through the entire scroll rather than
 * scrolling away at one viewport. The scroller itself lives in HomeScene.
 *
 * `shrink-0` is what lets it actually grow: the pane is a flex item of a
 * definite-height content track, and default flex-shrink would crush its main
 * size back down to the min-h-full floor — capping the sticky header's
 * containing block at one viewport.
 */
const EntityPane = ({ children, className }: IEntityPaneProps) => {
  return <div className={cx('flex min-h-full w-full shrink-0 flex-col', className)}>{children}</div>;
};

export default EntityPane;
